import React, { useState } from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { EmailService } from '../../services/email/emailService';
import { validateContactForm } from '../../services/email/utils/emailValidation';
import { trackConversion } from '../../utils/tracking/adsTracking';
import FormField from './fields/FormField';
import SelectField from './fields/SelectField';
import FormStatus from './status/FormStatus';
import SubmitButton from './buttons/SubmitButton';
import type { ContactFormData } from '../../types/contact';
import { productOptions } from '../../data/contact-options';

const initialFormData: ContactFormData = {
  name: '',
  email: '',
  company: '',
  phone: '',
  product: '',
  service: '',
  supportTier: '',
  message: ''
};

const ContactForm = () => {
  const { t } = useTranslatedContent('contact');
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateContactForm(formData)) {
      setError(t('form.validation'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await EmailService.sendContactForm(formData);
      setSuccess(true);
      setFormData(initialFormData);
      trackConversion('contactForm');
    } catch (err) {
      console.error('Form submission error:', err);
      setError(t('form.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset service when product changes
      ...(name === 'product' ? { service: '' } : {})
    }));
    if (error) setError(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label={t('form.name')}
          name="name"
          type="text"
          required
          value={formData.name}
          onChange={handleChange}
        />
        <FormField
          label={t('form.email')}
          name="email"
          type="email"
          required
          value={formData.email}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label={t('form.company')}
          name="company"
          type="text"
          required
          value={formData.company}
          onChange={handleChange}
        />
        <FormField
          label={t('form.phone')}
          name="phone"
          type="tel"
          required
          value={formData.phone}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SelectField
          label={t('form.product')}
          name="product"
          required
          value={formData.product}
          onChange={handleChange}
          options={productOptions}
        />
        <SelectField
          label={t('form.service')}
          name="service"
          required
          value={formData.service}
          onChange={handleChange}
          options={formData.product ? productOptions.find(p => p.value === formData.product)?.services || [] : []}
          disabled={!formData.product}
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-blue-100">
          {t('form.message')}
        </label>
        <textarea
          id="message"
          name="message"
          rows={4}
          className="mt-1 block w-full rounded-xl bg-white/10 border-white/10 text-blue-100 placeholder-blue-300 focus:border-blue-400 focus:ring-blue-400"
          value={formData.message}
          onChange={handleChange}
          required
        />
      </div>

      <FormStatus 
        error={error}
        success={success}
        successMessage={t('form.success')}
      />

      <SubmitButton loading={loading} />
    </form>
  );
};

export default ContactForm;